/**
 * JuiceEffects — Visual feedback effects for game feel.
 * Provides screen shake, hit flash, sparkles, particle bursts, etc.
 * All methods are static and take a Phaser.Scene reference.
 *
 * Usage: JuiceEffects.screenShake(scene, 0.005);
 */

import Phaser from 'phaser';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SoundManager, SfxKey } from '@/systems/SoundManager';
import { GOD } from '@/config/GodMode';

export class JuiceEffects {

  // ── Screen Shake ──
  static screenShake(scene: Phaser.Scene, intensity = 0.004, duration = 120): void {
    scene.cameras.main.shake(duration, intensity);
  }

  // ── Camera Flash ──
  static cameraFlash(scene: Phaser.Scene, color = 0xffffff, duration = 80, alpha = 0.15): void {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    scene.cameras.main.flash(duration, r, g, b, false, (_cam: Phaser.Cameras.Scene2D.Camera, progress: number) => {
      // fade alpha
      if (progress > 0.5) scene.cameras.main.setAlpha(1);
    });
  }

  // ── Hit Flash on a game object (white flash then restore) ──
  static hitFlash(obj: Phaser.GameObjects.Shape, duration = 80): void {
    const original = obj.fillColor;
    obj.setFillStyle(0xffffff);
    obj.scene.time.delayedCall(duration, () => {
      if (obj.active) obj.setFillStyle(original);
    });
  }

  // ── Scale Punch (quick scale up then back) ──
  static scalePunch(scene: Phaser.Scene, obj: Phaser.GameObjects.GameObject & { scaleX: number; scaleY: number }, scale = 1.3, duration = 100): void {
    scene.tweens.add({
      targets: obj,
      scaleX: scale,
      scaleY: scale,
      duration: duration / 2,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  // ── Particle Burst (small circles flying outward) ──
  static particleBurst(scene: Phaser.Scene, x: number, y: number, color = 0xffaa00, count = 8, speed = 60): void {
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const size = 1.5 + Math.random() * 2;
      const particle = scene.add.circle(x, y, size, color, 0.9).setDepth(30);

      const vx = Math.cos(angle) * speed * (0.7 + Math.random() * 0.6);
      const vy = Math.sin(angle) * speed * (0.7 + Math.random() * 0.6);
      const life = 200 + Math.random() * 200;

      scene.tweens.add({
        targets: particle,
        x: x + vx,
        y: y + vy,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: life,
        ease: 'Quad.easeOut',
        onComplete: () => particle.destroy(),
      });
    }
  }

  // ── Sparkle Ring (expanding ring of small dots) ──
  static sparkleRing(scene: Phaser.Scene, x: number, y: number, color = 0x88ccff, radius = 20): void {
    const count = 6;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const dot = scene.add.circle(x, y, 2, color, 0.8).setDepth(30);

      scene.tweens.add({
        targets: dot,
        x: x + Math.cos(angle) * radius,
        y: y + Math.sin(angle) * radius,
        alpha: 0,
        duration: 300,
        ease: 'Quad.easeOut',
        onComplete: () => dot.destroy(),
      });
    }
  }

  // ── Damage Number (floating text that drifts up) ──
  static damageNumber(scene: Phaser.Scene, x: number, y: number, amount: number, color = '#ff4444'): void {
    const txt = scene.add.text(x, y - 8 * GOD.popupStartOffsetMult, `-${amount}`, {
      fontSize: '11px', color, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(35);

    scene.tweens.add({
      targets: txt,
      y: y - 8 * GOD.popupStartOffsetMult - 22 * GOD.popupDistMult,
      alpha: 0,
      duration: 600 * GOD.popupDurationMult,
      ease: 'Quad.easeOut',
      onComplete: () => txt.destroy(),
    });
  }

  // ── Placement Sparkle (gentle confirm effect) ──
  static placementSparkle(scene: Phaser.Scene, x: number, y: number): void {
    JuiceEffects.sparkleRing(scene, x, y, 0x44ff88, 16);
    const glow = scene.add.circle(x, y, 12, 0x44ff88, 0.3).setDepth(29);
    scene.tweens.add({
      targets: glow,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 300,
      onComplete: () => glow.destroy(),
    });
  }

  // ── Death Burst (enemy or robot destroyed) ──
  static deathBurst(scene: Phaser.Scene, x: number, y: number, color = 0xff6644): void {
    // Fast expanding particles
    JuiceEffects.particleBurst(scene, x, y, color, 12, 70);
    JuiceEffects.particleBurst(scene, x, y, 0xffcc00, 5, 30);

    // Explosion sprite flash
    if (scene.textures.exists('fx_explosion')) {
      const exp = scene.add.image(x, y, 'fx_explosion').setDepth(28);
      exp.setScale(0.3);
      exp.setTint(color);
      scene.tweens.add({
        targets: exp,
        scaleX: 1.2, scaleY: 1.2, alpha: 0,
        duration: 280, ease: 'Power2',
        onComplete: () => exp.destroy(),
      });
    } else {
      const ring = scene.add.circle(x, y, 4, color, 0.5).setDepth(28);
      ring.setStrokeStyle(2, color, 0.7);
      scene.tweens.add({
        targets: ring, scaleX: 3, scaleY: 3, alpha: 0,
        duration: 250, onComplete: () => ring.destroy(),
      });
    }

    // Debris chunks — fly out and land on the ground, persist for a few seconds
    const debrisColors = [color, 0x884422, 0x666666, 0x443322, 0xaa5533];
    const debrisCount = 6 + Math.floor(Math.random() * 5);
    for (let i = 0; i < debrisCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 30 + Math.random() * 60;
      const size = 1.5 + Math.random() * 3;
      const dc = debrisColors[Math.floor(Math.random() * debrisColors.length)];
      const chunk = scene.add.rectangle(x, y, size, size * (0.5 + Math.random()), dc, 0.9)
        .setDepth(5).setAngle(Math.random() * 360);

      const landX = x + Math.cos(angle) * speed;
      const landY = y + Math.sin(angle) * speed * 0.7 + Math.random() * 10;

      // Arc flight to landing spot
      scene.tweens.add({
        targets: chunk,
        x: landX, y: landY,
        angle: chunk.angle + (Math.random() - 0.5) * 360,
        duration: 200 + Math.random() * 200,
        ease: 'Quad.easeOut',
        onComplete: () => {
          // Sit on ground, then fade after a few seconds
          chunk.setAlpha(0.6);
          scene.tweens.add({
            targets: chunk,
            alpha: 0,
            duration: 1500 + Math.random() * 2000,
            delay: 2000 + Math.random() * 1500,
            onComplete: () => chunk.destroy(),
          });
        },
      });
    }

    // Splatter puddle — dark stain under death location
    const splat = scene.add.ellipse(x, y + 2, 3, 2, 0x331111, 0.5).setDepth(4);
    scene.tweens.add({
      targets: splat,
      scaleX: 3 + Math.random() * 2,
      scaleY: 2 + Math.random(),
      duration: 300,
      ease: 'Quad.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: splat,
          alpha: 0,
          duration: 3000,
          delay: 3000,
          onComplete: () => splat.destroy(),
        });
      },
    });
  }

  // ── Wave Start Flash (brief screen tint) ──
  static waveStartFlash(scene: Phaser.Scene): void {
    JuiceEffects.cameraFlash(scene, 0xffcc00, 150, 0.1);
  }

  // ── Base Hit Pulse (red flash + shake) ──
  static baseHitPulse(scene: Phaser.Scene): void {
    JuiceEffects.cameraFlash(scene, 0xff2200, 100, 0.15);
    JuiceEffects.screenShake(scene, 0.006, 150);
  }

  // ── Explosion Effect (for missiles/kamikaze) ──
  static explosionEffect(scene: Phaser.Scene, x: number, y: number, radius = 40): void {
    JuiceEffects.particleBurst(scene, x, y, 0xff6600, 12, 80);
    JuiceEffects.particleBurst(scene, x, y, 0xffcc00, 6, 40);
    JuiceEffects.screenShake(scene, 0.005, 100);
  }

  // ── Kamikaze Explosion (massive: shrapnel, fire, smoke, shake) ──
  static kamikazeExplosion(scene: Phaser.Scene, x: number, y: number, radius = 60): void {
    // Heavy screen shake
    scene.cameras.main?.shake(350, 0.015);

    // ── Central flash (white → orange) ──
    const flash = scene.add.circle(x, y, 8, 0xffffff, 0.95).setDepth(200)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: flash,
      scaleX: 5, scaleY: 5, alpha: 0,
      duration: 250, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });

    // ── Fire ring (expanding) ──
    const ring = scene.add.circle(x, y, radius * 0.3, 0xff6600, 0)
      .setStrokeStyle(4, 0xff4400, 0.8).setDepth(199)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: ring,
      scaleX: 3, scaleY: 3, alpha: 0,
      duration: 400, ease: 'Quad.easeOut',
      onComplete: () => ring.destroy(),
    });

    // ── Fire particles (orange + yellow, fast outward) ──
    JuiceEffects.particleBurst(scene, x, y, 0xff6600, 18, 120);
    JuiceEffects.particleBurst(scene, x, y, 0xffcc00, 10, 80);
    JuiceEffects.particleBurst(scene, x, y, 0xff2200, 8, 100);

    // ── Shrapnel chunks (metal debris flying in arcs) ──
    const shrapnelColors = [0x888888, 0x666666, 0xaaaaaa, 0x555555, 0x444444];
    const shrapnelCount = 10 + Math.floor(Math.random() * 6);
    for (let i = 0; i < shrapnelCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 60 + Math.random() * 100;
      const size = 2 + Math.random() * 3;
      const col = shrapnelColors[Math.floor(Math.random() * shrapnelColors.length)];
      const chunk = scene.add.rectangle(x, y, size, size * (0.4 + Math.random() * 0.6), col, 0.95)
        .setDepth(29).setAngle(Math.random() * 360);

      const landX = x + Math.cos(angle) * speed;
      const landY = y + Math.sin(angle) * speed * 0.7;

      scene.tweens.add({
        targets: chunk,
        x: landX, y: landY,
        angle: chunk.angle + (Math.random() - 0.5) * 540,
        duration: 300 + Math.random() * 250,
        ease: 'Quad.easeOut',
        onComplete: () => {
          chunk.setAlpha(0.5);
          scene.tweens.add({
            targets: chunk, alpha: 0,
            duration: 2000 + Math.random() * 1500,
            delay: 1500 + Math.random() * 1000,
            onComplete: () => chunk.destroy(),
          });
        },
      });
    }

    // ── Smoke clouds (dark puffs rising) ──
    for (let i = 0; i < 5; i++) {
      const ox = (Math.random() - 0.5) * radius * 0.6;
      const oy = (Math.random() - 0.5) * radius * 0.4;
      const smokeSize = 8 + Math.random() * 12;
      const smoke = scene.add.circle(x + ox, y + oy, smokeSize, 0x222222, 0.5)
        .setDepth(28);
      scene.tweens.add({
        targets: smoke,
        y: y + oy - 30 - Math.random() * 20,
        scaleX: 2 + Math.random(), scaleY: 2 + Math.random(),
        alpha: 0,
        duration: 800 + Math.random() * 600,
        delay: i * 60,
        ease: 'Quad.easeOut',
        onComplete: () => smoke.destroy(),
      });
    }

    // ── Secondary fire puffs (delayed, orange) ──
    for (let i = 0; i < 3; i++) {
      const delay = 80 + i * 100;
      const ox = (Math.random() - 0.5) * radius * 0.8;
      const oy = (Math.random() - 0.5) * radius * 0.5;
      scene.time.delayedCall(delay, () => {
        const firePuff = scene.add.circle(x + ox, y + oy, 5, 0xff8800, 0.7)
          .setDepth(199).setBlendMode(Phaser.BlendModes.ADD);
        scene.tweens.add({
          targets: firePuff,
          scaleX: 3, scaleY: 3, alpha: 0,
          duration: 250,
          onComplete: () => firePuff.destroy(),
        });
      });
    }

    // ── Ground scorch mark ──
    const scorch = scene.add.ellipse(x, y + 3, radius * 0.8, radius * 0.4, 0x111111, 0.4)
      .setDepth(3);
    scene.tweens.add({
      targets: scorch, alpha: 0,
      duration: 4000, delay: 3000,
      onComplete: () => scorch.destroy(),
    });
  }

  // ── NUKE Explosion (Lv3 explosive charge) ──
  // Reuses the nuke_boom spritesheet + adds massive shockwave, flash, and particles
  static nukeExplosion(scene: Phaser.Scene, x: number, y: number): void {
    const radius = 150; // visual blast radius

    // Heavy screen shake + white flash
    scene.cameras.main?.shake(500, 0.025);
    JuiceEffects.cameraFlash(scene, 0xffffff, 500, 0.9);

    // Expanding shockwave ring
    const ring = scene.add.circle(x, y, 10, 0xffffff, 0.6).setDepth(201)
      .setStrokeStyle(4, 0xffcc00, 0.8);
    const ringState = { r: 10 };
    scene.tweens.add({
      targets: ringState, r: radius,
      duration: 700, ease: 'Quad.easeOut',
      onUpdate: () => { ring.setRadius(ringState.r); ring.setAlpha(1 - ringState.r / radius); },
      onComplete: () => ring.destroy(),
    });

    // Nuke spritesheet animation
    if (scene.anims.exists('nuke_boom')) {
      const explosion = scene.add.sprite(x, y, 'nuke_explosion').setDepth(202);
      const targetSize = radius * 1.2;
      explosion.setDisplaySize(targetSize, targetSize * (243 / 256));
      explosion.play('nuke_boom');
      explosion.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        scene.tweens.add({
          targets: explosion, alpha: 0, duration: 1200, ease: 'Quad.easeIn',
          onComplete: () => explosion.destroy(),
        });
      });
    }

    // Central flash (white → orange)
    const flash = scene.add.circle(x, y, 12, 0xffffff, 1).setDepth(203)
      .setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: flash, scaleX: 8, scaleY: 8, alpha: 0,
      duration: 350, ease: 'Cubic.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Massive particle burst
    JuiceEffects.particleBurst(scene, x, y, 0xff6600, 30, 180);
    JuiceEffects.particleBurst(scene, x, y, 0xffcc00, 20, 140);
    JuiceEffects.particleBurst(scene, x, y, 0xff2200, 15, 160);

    // Shrapnel chunks (more than standard kamikaze)
    const shrapnelColors = [0x888888, 0x666666, 0xaaaaaa, 0x555555];
    for (let i = 0; i < 20; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 100 + Math.random() * 150;
      const size = 2 + Math.random() * 4;
      const col = shrapnelColors[Math.floor(Math.random() * shrapnelColors.length)];
      const chunk = scene.add.rectangle(x, y, size, size * (0.4 + Math.random() * 0.6), col, 0.95)
        .setDepth(29).setAngle(Math.random() * 360);
      scene.tweens.add({
        targets: chunk,
        x: x + Math.cos(angle) * speed, y: y + Math.sin(angle) * speed * 0.7,
        angle: chunk.angle + (Math.random() - 0.5) * 540,
        duration: 350 + Math.random() * 300, ease: 'Quad.easeOut',
        onComplete: () => {
          scene.tweens.add({ targets: chunk, alpha: 0, duration: 2000, delay: 1500, onComplete: () => chunk.destroy() });
        },
      });
    }

    // Large smoke clouds
    for (let i = 0; i < 8; i++) {
      const ox = (Math.random() - 0.5) * radius * 0.6;
      const oy = (Math.random() - 0.5) * radius * 0.4;
      const smoke = scene.add.circle(x + ox, y + oy, 12 + Math.random() * 15, 0x222222, 0.5).setDepth(28);
      scene.tweens.add({
        targets: smoke, y: y + oy - 40 - Math.random() * 30,
        scaleX: 2.5 + Math.random(), scaleY: 2.5 + Math.random(), alpha: 0,
        duration: 1000 + Math.random() * 600, delay: i * 50, ease: 'Quad.easeOut',
        onComplete: () => smoke.destroy(),
      });
    }

    // Ground scorch mark (larger)
    const scorch = scene.add.circle(x, y, radius * 0.5, 0x111111, 0.25).setDepth(1);
    scene.tweens.add({
      targets: scorch, alpha: 0, duration: 5000, delay: 3000,
      onComplete: () => scorch.destroy(),
    });
  }

  // ── Kamikaze War Cry Bubble ──
  static kamikazeWarCry(scene: Phaser.Scene, x: number, y: number, radius: number): void {
    const cries = [
      'LEEROY!!', 'YOLO!', 'BOOM!', 'À L\'ATTAQUE!',
      'BANZAI!', 'WITNESS ME!', 'FOR GLORY!',
      'COWABUNGA!', 'GERONIMO!', 'AU REVOIR!',
      '💣💥', 'C\'EST PARTI!', 'ADIEU!',
      'SHINY AND CHROME!', 'VALHALLA!', 'I LIVE, I DIE, I LIVE AGAIN!',
      'RIDE ETERNAL!', 'MEDIOCRE!', 'WHAT A LOVELY DAY!',
    ];
    const cry = cries[Math.floor(Math.random() * cries.length)];

    // Speech bubble background
    const bubbleY = y - radius - 22;
    const txt = scene.add.text(x, bubbleY, cry, {
      fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold',
      color: '#ffffff', stroke: '#000000', strokeThickness: 3,
      backgroundColor: '#cc4400',
      padding: { x: 4, y: 2 },
    }).setOrigin(0.5).setDepth(300).setScale(0);

    // Pop in
    scene.tweens.add({
      targets: txt,
      scaleX: 1.2, scaleY: 1.2,
      duration: 150,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Drift up and fade
        scene.tweens.add({
          targets: txt,
          y: bubbleY - 18, alpha: 0,
          scaleX: 0.8, scaleY: 0.8,
          duration: 1200, delay: 600,
          ease: 'Quad.easeOut',
          onComplete: () => txt.destroy(),
        });
      },
    });
  }

  // ── Resource Gain Popup (coin icon + "+5" floating green text) ──
  static resourceGainPopup(scene: Phaser.Scene, x: number, y: number, amount: number): void {
    SoundManager.play('coin');
    const py = y - 5 * GOD.popupStartOffsetMult;
    const ico = scene.textures.exists('icon_coin')
      ? scene.add.image(x - 14, py, 'icon_coin').setDisplaySize(14, 14).setDepth(36)
      : null;
    const txt = scene.add.text(ico ? x : x, py, `+${amount}`, {
      fontSize: '12px', color: '#44ff88', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(ico ? 0 : 0.5, 0.5).setDepth(36);

    const targets = ico ? [txt, ico] : [txt];
    scene.tweens.add({
      targets,
      y: py - 30 * GOD.popupDistMult,
      alpha: 0,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 700 * GOD.popupDurationMult,
      ease: 'Quad.easeOut',
      onComplete: () => { txt.destroy(); ico?.destroy(); },
    });
  }

  // ── Loot Fly-to-HUD (icon pops, bounces on ground with 3D spin, then arcs to HUD) ──
  // Generic: works for coins, Co, Electro — just pass texture key + text color
  static lootFlyToHud(
    scene: Phaser.Scene,
    screenX: number, screenY: number,
    targetX: number, targetY: number,
    amount: number,
    delay: number,
    onArrive: () => void,
    textureKey = 'icon_coin',
    textColor = '#ffdd44',
    sfx: SfxKey = 'coin',
  ): void {
    const iconSize = GOD.coinFlyIconSize;
    const groundY = screenY;
    const scatterX = screenX + (Math.random() - 0.5) * 20;
    const texW = scene.textures.exists(textureKey) ? scene.textures.get(textureKey).getSourceImage().width : 128;
    const fullScaleX = iconSize / texW;
    const fullScaleY = iconSize / texW;

    const ico = scene.add.image(scatterX, groundY, textureKey)
      .setDisplaySize(iconSize, iconSize).setScale(0).setDepth(200).setAlpha(0.95);

    const txt = scene.add.text(scatterX + 12, groundY, `+${amount}`, {
      fontSize: '11px', color: textColor, fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(200).setAlpha(0);

    // Randomize bounce count ±1
    const baseBounces = GOD.coinFlyBounceCount;
    const bounceCount = Math.max(1, baseBounces + Math.round((Math.random() - 0.5) * 2));
    const popHeight = GOD.coinFlyPopHeight;
    const bounceDur = GOD.coinFlyBounceDuration;
    const damping = GOD.coinFlyBounceDamping;
    const spinSpeed = GOD.coinFlySpinSpeed;

    // Lateral drift per bounce (coin rolls slightly)
    const lateralDrift = (Math.random() - 0.5) * 30;

    // Phase 1: initial pop upward
    scene.tweens.add({
      targets: ico,
      scaleY: fullScaleY,
      duration: 150,
      delay,
      ease: 'Back.easeOut',
      onStart: () => {
        SoundManager.play(sfx);
        ico.setScale(fullScaleX, 0); // start flat
        scene.tweens.add({ targets: txt, alpha: 1, y: groundY - popHeight - 15, duration: 200 });
      },
    });

    // Build bounce sequence: each bounce is a parabolic arc with decreasing height
    // We use a counter-based approach with recursive scheduling
    let totalTime = delay;
    const initialDur = bounceDur * 1.5; // first arc is longer (initial pop)

    // Schedule the entire bounce+spin sequence via a single onUpdate tween
    // that tracks elapsed time and computes Y position + spin analytically
    const bounceHeights: number[] = [];
    const bounceDurations: number[] = [];
    let h = popHeight;
    // First arc (pop)
    bounceHeights.push(h);
    bounceDurations.push(initialDur);
    // Subsequent bounces
    for (let i = 0; i < bounceCount; i++) {
      h *= damping;
      bounceHeights.push(h);
      bounceDurations.push(bounceDur * Math.pow(damping, i * 0.5));
    }
    const totalBounceDuration = bounceDurations.reduce((a, b) => a + b, 0);

    // Master tween for bounce phase
    const dummyTarget = { progress: 0 };
    scene.tweens.add({
      targets: dummyTarget,
      progress: 1,
      duration: totalBounceDuration,
      delay: delay + 50, // slight offset after scale pop
      ease: 'Linear',
      onUpdate: () => {
        const elapsed = dummyTarget.progress * totalBounceDuration;

        // Determine which bounce arc we're in
        let cumTime = 0;
        let arcIdx = 0;
        for (let i = 0; i < bounceDurations.length; i++) {
          if (elapsed < cumTime + bounceDurations[i]) { arcIdx = i; break; }
          cumTime += bounceDurations[i];
          arcIdx = i;
        }
        const arcElapsed = elapsed - cumTime;
        const arcDur = bounceDurations[arcIdx];
        const t = Math.min(1, arcElapsed / arcDur);
        const arcH = bounceHeights[arcIdx];

        // Parabolic arc: y = ground - 4h*t*(1-t)  (peaks at t=0.5)
        const bounceOffset = 4 * arcH * t * (1 - t);
        ico.y = groundY - bounceOffset;

        // Lateral drift
        ico.x = scatterX + lateralDrift * (elapsed / totalBounceDuration);

        // 3D coin spin: oscillate scaleX to simulate rotation around Y axis
        const spinPhase = elapsed * spinSpeed * 0.01;
        const spinScale = Math.cos(spinPhase) * fullScaleX;
        ico.scaleX = spinScale === 0 ? 0.01 : spinScale; // avoid 0 scale
      },
      onComplete: () => {
        // Fade out "+N" text
        scene.tweens.add({
          targets: txt, alpha: 0, duration: 200,
          onComplete: () => txt.destroy(),
        });

        // Settle: restore full scale and brief pause
        ico.scaleX = fullScaleX;
        ico.y = groundY;

        // Phase 2: arc flight to HUD
        const startX = ico.x;
        const startY = ico.y;
        const dy = targetY - startY;
        const arcH = GOD.coinFlyArcHeight;

        scene.tweens.add({
          targets: ico,
          x: targetX,
          scaleX: fullScaleX * 0.5,
          scaleY: fullScaleY * 0.5,
          duration: GOD.coinFlyDuration,
          ease: 'Quad.easeIn',
          onUpdate: (tween: Phaser.Tweens.Tween) => {
            const p = tween.progress;
            ico.y = startY + dy * p - Math.sin(p * Math.PI) * arcH;
            // Continue spinning during flight (slower)
            ico.scaleX = Math.cos(p * spinSpeed * 2) * fullScaleX * (1 - p * 0.5);
          },
          onComplete: () => {
            ico.destroy();
            onArrive();
          },
        });
      },
    });
  }

  /** Convenience: coin fly with default texture/color */
  static coinFlyToHud(
    scene: Phaser.Scene, screenX: number, screenY: number,
    targetX: number, targetY: number, amount: number, delay: number, onArrive: () => void,
  ): void {
    JuiceEffects.lootFlyToHud(scene, screenX, screenY, targetX, targetY, amount, delay, onArrive, 'icon_coin', '#ffdd44', 'coin');
  }

  // ── Harvest Loot Fly (icons pop from ground and fly to robot) ──
  static harvestLootFly(
    scene: Phaser.Scene,
    tileX: number, tileY: number,
    robotX: number, robotY: number,
    co: number, electro: number,
  ): void {
    const items: { tex: string; count: number; color: number }[] = [];
    if (co > 0) items.push({ tex: 'icon_co', count: Math.min(3, Math.ceil(co / 2)), color: 0x44ee88 });
    if (electro > 0) items.push({ tex: 'icon_electro', count: Math.min(3, Math.ceil(electro / 2)), color: 0x88ccff });

    const iconSize = 32;
    let delay = 0;

    for (const item of items) {
      for (let i = 0; i < item.count; i++) {
        const scatterX = tileX + (Math.random() - 0.5) * 16;
        const jumpY = tileY - 15 - Math.random() * 10;
        const d = delay;

        // Phase 1: pop from ground (0-200ms)
        const ico = scene.add.image(scatterX, tileY, item.tex)
          .setDisplaySize(iconSize, iconSize).setScale(0).setDepth(35).setAlpha(0.9);

        scene.tweens.add({
          targets: ico,
          scaleX: iconSize / ico.width,
          scaleY: iconSize / ico.height,
          y: jumpY,
          duration: 200,
          delay: d,
          ease: 'Back.easeOut',
          onComplete: () => {
            // Phase 2: brief float (200ms)
            scene.tweens.add({
              targets: ico,
              y: jumpY - 3,
              duration: 200,
              yoyo: true,
              ease: 'Sine.easeInOut',
              onComplete: () => {
                // Phase 3: fly to robot (400ms)
                scene.tweens.add({
                  targets: ico,
                  x: robotX,
                  y: robotY,
                  scaleX: ico.scaleX * 0.5,
                  scaleY: ico.scaleY * 0.5,
                  alpha: 0.3,
                  duration: 400,
                  ease: 'Quad.easeIn',
                  onComplete: () => {
                    // Arrival burst
                    JuiceEffects.sparkleRing(scene, robotX, robotY, item.color, 12);
                    ico.destroy();
                  },
                });
              },
            });
          },
        });

        delay += 60; // stagger each icon slightly
      }
    }
  }

  // ── Combo qualifiers by streak count (cybernetic/robotic/war-like) ──
  private static readonly COMBO_QUALIFIERS: Record<number, string[]> = {
    3:  ['LOCK-ON!', 'ARMED!', 'ENGAGED!', 'PRIMED!', 'LOADED!'],
    5:  ['OVERDRIVE!', 'BALLISTIC!', 'CHARGED!', 'ASSAULT!', 'BARRAGE!'],
    10: ['ANNIHILATION!', 'MELTDOWN!', 'OMEGA STRIKE!', 'KILLSWITCH!', 'TOTAL WAR!'],
    15: ['SINGULARITY!', 'EXTINCTION!', 'ARMAGEDDON!', 'GODMODE!', 'OVERKILL!'],
    20: ['RAGNARÖK!', 'APOCALYPSE!', 'DOOMSDAY!', 'CATACLYSM!', 'OBLIVION!'],
  };

  private static getComboQualifier(count: number): string {
    // Find the highest threshold <= count
    const thresholds = Object.keys(JuiceEffects.COMBO_QUALIFIERS).map(Number).sort((a, b) => b - a);
    for (const t of thresholds) {
      if (count >= t) {
        const pool = JuiceEffects.COMBO_QUALIFIERS[t];
        return pool[Math.floor(Math.random() * pool.length)];
      }
    }
    return 'COMBO!';
  }

  // ── Kill Streak Popup ("x3 COMBO!" + qualifier scaling text) ──
  static killStreakPopup(scene: Phaser.Scene, x: number, y: number, count: number): void {
    const qualifier = JuiceEffects.getComboQualifier(count);
    const colors: Record<number, string> = { 3: '#ffcc00', 5: '#ff8800', 10: '#ff3300', 15: '#ff00ff', 20: '#00ffff' };
    const thresholds = [20, 15, 10, 5, 3];
    const color = colors[thresholds.find(t => count >= t) ?? 3] ?? '#ffcc00';

    const txt = scene.add.text(x, y - 20 * GOD.popupStartOffsetMult, `x${count} ${qualifier}`, {
      fontSize: '16px', color, fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#442200', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(38).setScale(0.3);

    scene.tweens.add({
      targets: txt,
      scaleX: 1.4,
      scaleY: 1.4,
      duration: 200 * GOD.popupDurationMult,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: txt,
          y: y - 20 * GOD.popupStartOffsetMult - 30 * GOD.popupDistMult,
          alpha: 0,
          scaleX: 0.8,
          scaleY: 0.8,
          duration: 500 * GOD.popupDurationMult,
          ease: 'Quad.easeIn',
          onComplete: () => txt.destroy(),
        });
      },
    });

    JuiceEffects.sparkleRing(scene, x, y, 0xffcc00, 25);
  }

  // ── Wave Clear Celebration ──
  static waveClearCelebration(scene: Phaser.Scene): void {
    JuiceEffects.cameraFlash(scene, 0xffcc00, 250, 0.12);

    const cx = scene.cameras.main.worldView.centerX;
    const cy = scene.cameras.main.worldView.centerY;

    const txt = scene.add.text(cx, cy, 'WAVE CLEAR!', {
      fontSize: '22px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#443300', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(40).setScale(0.5);

    scene.tweens.add({
      targets: txt,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 300,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: txt,
          alpha: 0,
          y: cy - 40 * GOD.popupDistMult,
          duration: 800 * GOD.popupDurationMult,
          delay: 400 * GOD.popupDurationMult,
          ease: 'Quad.easeIn',
          onComplete: () => txt.destroy(),
        });
      },
    });

    // Sparkle burst around center
    for (let i = 0; i < 3; i++) {
      scene.time.delayedCall(i * 100, () => {
        const ox = (Math.random() - 0.5) * 120;
        const oy = (Math.random() - 0.5) * 80;
        JuiceEffects.sparkleRing(scene, cx + ox, cy + oy, 0xffdd44, 30);
      });
    }
  }

  // ── Enemy Spawn Pop-in (scale from 0 + flash) ──
  static spawnPopIn(scene: Phaser.Scene, enemy: { body: Phaser.GameObjects.Image }): void {
    enemy.body.setScale(0);
    scene.tweens.add({
      targets: enemy.body,
      scaleX: enemy.body.scaleX || 1,
      scaleY: enemy.body.scaleY || 1,
      duration: 200,
      ease: 'Back.easeOut',
    });
    // Brief red glow at spawn point
    const glow = scene.add.circle(enemy.body.x, enemy.body.y, 10, 0xff4444, 0.4).setDepth(9);
    scene.tweens.add({
      targets: glow, scaleX: 2, scaleY: 2, alpha: 0,
      duration: 300, onComplete: () => glow.destroy(),
    });
  }

  // ── Muzzle Flash (bright spark at weapon barrel) ──
  static muzzleFlash(scene: Phaser.Scene, x: number, y: number, color = 0xffaa00): void {
    const flash = scene.add.circle(x, y, 3, 0xffffff, 0.9).setDepth(25);
    scene.tweens.add({
      targets: flash, scaleX: 2.5, scaleY: 2.5, alpha: 0,
      duration: 100, onComplete: () => flash.destroy(),
    });
    // 3 tiny sparks
    for (let i = 0; i < 3; i++) {
      const angle = Math.random() * Math.PI * 2;
      const sp = scene.add.circle(x, y, 1, color, 0.8).setDepth(25);
      scene.tweens.add({
        targets: sp,
        x: x + Math.cos(angle) * (10 + Math.random() * 10),
        y: y + Math.sin(angle) * (10 + Math.random() * 10),
        alpha: 0, duration: 120 + Math.random() * 80,
        onComplete: () => sp.destroy(),
      });
    }
  }

  // ── Heal Sparkle (green rising particles around a unit) ──
  static healSparkle(scene: Phaser.Scene, x: number, y: number): void {
    for (let i = 0; i < 6; i++) {
      const ox = (Math.random() - 0.5) * 24;
      const startY = y + 8 + Math.random() * 8;
      const dot = scene.add.circle(x + ox, startY, 1.5, 0x44ff88, 0.8).setDepth(201);
      scene.tweens.add({
        targets: dot,
        y: startY - 20 - Math.random() * 15,
        alpha: 0,
        duration: 400 + Math.random() * 300,
        delay: i * 50,
        onComplete: () => dot.destroy(),
      });
    }
  }

  // ── Knockback Impact (spark trail at origin) ──
  static knockbackImpact(scene: Phaser.Scene, x: number, y: number): void {
    JuiceEffects.particleBurst(scene, x, y, 0xffcc44, 5, 30);
    JuiceEffects.screenShake(scene, 0.003, 60);
  }

  // ── Component Unlock Effect ──
  static componentUnlockEffect(scene: Phaser.Scene, x: number, y: number): void {
    JuiceEffects.sparkleRing(scene, x, y, 0x88aaff, 24);
    JuiceEffects.particleBurst(scene, x, y, 0x88aaff, 8, 40);

    const glow = scene.add.circle(x, y, 16, 0x88aaff, 0.4).setDepth(30);
    scene.tweens.add({
      targets: glow,
      scaleX: 2.5,
      scaleY: 2.5,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => glow.destroy(),
    });
  }

  // ── Component Evolution Effect (juicy upgrade feedback) ──
  static componentEvolveEffect(scene: Phaser.Scene, x: number, y: number): void {
    // Gold sparkle ring
    JuiceEffects.sparkleRing(scene, x, y, 0xffcc00, 30);
    JuiceEffects.sparkleRing(scene, x, y, 0x44ff88, 20);
    // Particle burst outward (gold + green)
    JuiceEffects.particleBurst(scene, x, y, 0xffcc00, 10, 50);
    JuiceEffects.particleBurst(scene, x, y, 0x44ff88, 6, 35);

    // Expanding glow ring
    const glow = scene.add.circle(x, y, 12, 0xffcc00, 0.5).setDepth(50);
    scene.tweens.add({
      targets: glow,
      scaleX: 3, scaleY: 3,
      alpha: 0,
      duration: 500,
      ease: 'Quad.easeOut',
      onComplete: () => glow.destroy(),
    });

    // "LEVEL UP!" floating text
    const txt = scene.add.text(x, y - 10, 'LEVEL UP!', {
      fontSize: '14px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
      stroke: '#442200', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(51).setScale(0.3);

    scene.tweens.add({
      targets: txt,
      scaleX: 1.2, scaleY: 1.2,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        scene.tweens.add({
          targets: txt,
          y: y - 40, alpha: 0,
          duration: 600,
          ease: 'Quad.easeIn',
          onComplete: () => txt.destroy(),
        });
      },
    });
  }

  // ── Named event handlers (stored for proper cleanup) ──
  private static _onEnemySpawned: ((enemy: any) => void) | null = null;
  private static _onEnemyDestroyed: ((enemy: any) => void) | null = null;
  private static _onEnemyReachedBase: (() => void) | null = null;
  private static _onBaseDamaged: (() => void) | null = null;
  private static _onWaveStarted: (() => void) | null = null;
  private static _onRobotPlaced: ((data: any) => void) | null = null;
  private static _onRobotExploded: ((data: any) => void) | null = null;
  private static _onKamikazeWarCry: ((data: any) => void) | null = null;
  private static _onPursueActivated: ((data: any) => void) | null = null;
  private static _onMissileExploded: (() => void) | null = null;
  private static _onRobotFired: ((data: any) => void) | null = null;
  private static _onKillStreak: ((data: any) => void) | null = null;
  private static _onWaveCelebration: (() => void) | null = null;
  private static _onComponentUnlocked: ((data: any) => void) | null = null;
  private static _onCriticalHit: ((data: any) => void) | null = null;
  private static _onJumperPush: ((data: any) => void) | null = null;
  private static _onTerrainDeath: ((data: any) => void) | null = null;
  private static _onFloatingWarning: ((data: any) => void) | null = null;
  private static _onBilliardWallBounce: ((data: any) => void) | null = null;
  private static _onBilliardEnemyCollision: ((data: any) => void) | null = null;
  private static _onBilliardKnockbackStart: ((data: any) => void) | null = null;
  private static _onBilliardKnockbackEnd: ((data: any) => void) | null = null;
  private static _onCrtGlitch: ((data: any) => void) | null = null;

  // ── Connect to EventBus (call once from GameScene) ──
  static connectToEvents(scene: Phaser.Scene): void {
    // Enemy spawn pop-in animation
    JuiceEffects._onEnemySpawned = (enemy: { body: Phaser.GameObjects.Image }) => {
      // Save target scale before pop-in overrides it
      const targetScale = enemy.body.scaleX;
      enemy.body.setScale(0);
      scene.tweens.add({
        targets: enemy.body,
        scaleX: targetScale, scaleY: targetScale,
        duration: 200, ease: 'Back.easeOut',
      });
      // Depth below fog of war (depth 5) so the glow is hidden under fog
      const glow = scene.add.circle(enemy.body.x, enemy.body.y, 10, 0xff4444, 0.4).setDepth(3);
      scene.tweens.add({
        targets: glow, scaleX: 2, scaleY: 2, alpha: 0,
        duration: 300, onComplete: () => glow.destroy(),
      });
    };
    EventBus.on(GameEvents.ENEMY_SPAWNED, JuiceEffects._onEnemySpawned);

    JuiceEffects._onEnemyDestroyed = (enemy: { body?: { x: number; y: number } }) => {
      if (enemy.body) {
        JuiceEffects.deathBurst(scene, enemy.body.x, enemy.body.y, 0xff6644);
      }
    };
    EventBus.on(GameEvents.ENEMY_DESTROYED, JuiceEffects._onEnemyDestroyed);

    JuiceEffects._onEnemyReachedBase = () => {
      JuiceEffects.baseHitPulse(scene);
    };
    EventBus.on(GameEvents.ENEMY_REACHED_BASE, JuiceEffects._onEnemyReachedBase);

    JuiceEffects._onBaseDamaged = () => {
      JuiceEffects.baseHitPulse(scene);
    };
    EventBus.on(GameEvents.BASE_DAMAGED, JuiceEffects._onBaseDamaged);

    JuiceEffects._onWaveStarted = () => {
      JuiceEffects.waveStartFlash(scene);
    };
    EventBus.on(GameEvents.WAVE_STARTED, JuiceEffects._onWaveStarted);

    JuiceEffects._onRobotPlaced = (data: { x?: number; y?: number }) => {
      if (data.x != null && data.y != null) {
        JuiceEffects.placementSparkle(scene, data.x, data.y);
      }
    };
    EventBus.on(GameEvents.ROBOT_PLACED, JuiceEffects._onRobotPlaced);

    JuiceEffects._onRobotExploded = (data: { x?: number; y?: number; evoLevel?: number }) => {
      if (data.x != null && data.y != null) {
        if ((data.evoLevel ?? 0) >= 3) {
          // Lv3 NUKE: full nuke animation with shockwave + sprite
          JuiceEffects.nukeExplosion(scene, data.x, data.y);
        } else {
          JuiceEffects.kamikazeExplosion(scene, data.x, data.y, 60);
        }
        SoundManager.play('explosion');
      }
    };
    EventBus.on(GameEvents.ROBOT_EXPLODED, JuiceEffects._onRobotExploded);

    JuiceEffects._onKamikazeWarCry = (data: { x: number; y: number; radius: number }) => {
      JuiceEffects.kamikazeWarCry(scene, data.x, data.y, data.radius);
    };
    EventBus.on(GameEvents.KAMIKAZE_WAR_CRY, JuiceEffects._onKamikazeWarCry);

    JuiceEffects._onPursueActivated = (data: { x: number; y: number; radius: number }) => {
      JuiceEffects.pursueActivation(scene, data.x, data.y, data.radius);
    };
    EventBus.on(GameEvents.PURSUE_ACTIVATED, JuiceEffects._onPursueActivated);

    JuiceEffects._onMissileExploded = () => {
      JuiceEffects.screenShake(scene, 0.003, 80);
    };
    EventBus.on(GameEvents.MISSILE_EXPLODED, JuiceEffects._onMissileExploded);

    // Muzzle flash + subtle screen nudge on robot fire
    JuiceEffects._onRobotFired = (data: { robot?: { x: number; y: number }; enemy?: { body?: { x: number; y: number } }; weaponType?: string }) => {
      JuiceEffects.screenShake(scene, 0.001, 30);
      if (data.robot && data.enemy?.body) {
        // Flash at midpoint between robot and enemy (near muzzle)
        const dx = data.enemy.body.x - data.robot.x;
        const dy = data.enemy.body.y - data.robot.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 1) {
          const mx = data.robot.x + (dx / dist) * 18;
          const my = data.robot.y + (dy / dist) * 18;
          const color = data.weaponType?.includes('LASER') ? 0x44aaff
                      : data.weaponType?.includes('FLAME') ? 0xff4400
                      : 0xffaa00;
          JuiceEffects.muzzleFlash(scene, mx, my, color);
        }
      }
    };
    EventBus.on(GameEvents.ROBOT_FIRED, JuiceEffects._onRobotFired);

    // Kill streak popup
    JuiceEffects._onKillStreak = (data: { count: number; x: number; y: number }) => {
      JuiceEffects.killStreakPopup(scene, data.x, data.y, data.count);
    };
    EventBus.on(GameEvents.KILL_STREAK, JuiceEffects._onKillStreak);

    // Wave clear celebration (fired after repair sequence finishes)
    JuiceEffects._onWaveCelebration = () => {
      JuiceEffects.waveClearCelebration(scene);
      SoundManager.play('waveClear');
    };
    EventBus.on(GameEvents.WAVE_CELEBRATION, JuiceEffects._onWaveCelebration);

    // Component unlock sparkle
    JuiceEffects._onComponentUnlocked = (data: { x?: number; y?: number }) => {
      if (data.x != null && data.y != null) {
        JuiceEffects.componentUnlockEffect(scene, data.x, data.y);
      }
    };
    EventBus.on(GameEvents.COMPONENT_UNLOCKED, JuiceEffects._onComponentUnlocked);

    // Critical hit impact — gold sparkle ring + subtle screen shake
    JuiceEffects._onCriticalHit = (data: { x?: number; y?: number }) => {
      if (data.x != null && data.y != null) {
        JuiceEffects.critImpact(scene, data.x, data.y);
      }
    };
    EventBus.on(GameEvents.CRITICAL_HIT, JuiceEffects._onCriticalHit);

    // Jumper push — spring animation: slide enemy + spring particles + vibration
    JuiceEffects._onJumperPush = (data: {
      target: { body: Phaser.GameObjects.Image; alive: boolean };
      startX: number; startY: number;
      endX: number; endY: number;
      dirX: number; dirY: number;
    }) => {
      const { target, startX, startY, endX, endY, dirX, dirY } = data;

      // Spring particle burst at push origin
      const springColor = 0x44dd88;
      for (let i = 0; i < 6; i++) {
        const angle = Math.atan2(dirY, dirX) + (Math.random() - 0.5) * 1.2;
        const speed = 40 + Math.random() * 30;
        const p = scene.add.circle(startX, startY, 2, springColor, 0.8).setDepth(25);
        scene.tweens.add({
          targets: p,
          x: startX + Math.cos(angle) * speed,
          y: startY + Math.sin(angle) * speed,
          alpha: 0, scaleX: 0.3, scaleY: 0.3,
          duration: 250 + Math.random() * 100,
          onComplete: () => p.destroy(),
        });
      }

      // Slide enemy body from start to end — only in legacy mode
      // In billiard mode, BilliardKnockback system handles movement physics
      if (!GOD.billiardEnabled && target.alive && target.body) {
        target.body.setPosition(startX, startY);
        const slideDist = Math.sqrt((endX - startX) ** 2 + (endY - startY) ** 2);
        const slideDur = Math.min(600, Math.max(200, slideDist * 3));
        scene.tweens.add({
          targets: target.body,
          x: endX, y: endY,
          duration: slideDur,
          ease: 'Power2',
        });
      }

      // Small screen vibration
      JuiceEffects.screenShake(scene, 0.003, 80);
    };
    EventBus.on(GameEvents.JUMPER_PUSH, JuiceEffects._onJumperPush);

    // ── Terrain death animations (drowning / burning / falling) ──
    JuiceEffects._onTerrainDeath = (data: {
      enemy: { body: Phaser.GameObjects.Image; hpBar: Phaser.GameObjects.Rectangle; hpBarBg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text; groundShadow?: Phaser.GameObjects.Ellipse };
      type: 'water' | 'magma' | 'chasm';
      duration: number;
    }) => {
      const { enemy, type, duration } = data;
      const b = enemy.body;
      if (!b || !b.active) return;
      const ms = duration * 1000;

      if (type === 'water') {
        // Drowning: snap to water tile center, flatten in-place + fade + blue tint
        const TS = 40; // TILE_SIZE
        const tileCX = Math.floor(b.x / TS) * TS + TS / 2;
        const tileCY = Math.floor(b.y / TS) * TS + TS / 2;
        b.setPosition(tileCX, tileCY);
        b.setTint(0x4488cc);
        scene.tweens.add({
          targets: b,
          alpha: 0,
          scaleX: b.scaleX * 0.5,
          scaleY: b.scaleY * 0.2,
          duration: ms,
          ease: 'Quad.easeIn',
        });
        // Bobbing wobble
        scene.tweens.add({
          targets: b,
          x: tileCX + 3,
          duration: 300,
          yoyo: true,
          repeat: Math.floor(ms / 600),
          ease: 'Sine.easeInOut',
        });
        // Bubble particles
        const bubbleCount = 6;
        for (let i = 0; i < bubbleCount; i++) {
          const delay = (ms / bubbleCount) * i + Math.random() * 200;
          scene.time.delayedCall(delay, () => {
            if (!b.active) return;
            const bub = scene.add.circle(
              b.x + (Math.random() - 0.5) * 16,
              b.y,
              2 + Math.random() * 2,
              0x88ccff, 0.6,
            ).setDepth(b.depth + 1);
            scene.tweens.add({
              targets: bub,
              y: bub.y - 15 - Math.random() * 10,
              alpha: 0,
              scaleX: 1.5, scaleY: 1.5,
              duration: 400 + Math.random() * 200,
              onComplete: () => bub.destroy(),
            });
          });
        }
      } else if (type === 'magma') {
        // Burning: red/orange tint, shake, shrink, fire particles
        b.setTint(0xff4400);
        scene.tweens.add({
          targets: b,
          scaleX: b.scaleX * 0.3,
          scaleY: b.scaleY * 0.2,
          alpha: 0,
          duration: ms,
          ease: 'Quad.easeIn',
        });
        // Rapid trembling
        scene.tweens.add({
          targets: b,
          x: b.x + 2,
          duration: 50,
          yoyo: true,
          repeat: Math.floor(ms / 100),
        });
        // Fire/smoke particles
        const fireCount = 10;
        for (let i = 0; i < fireCount; i++) {
          const delay = (ms / fireCount) * i + Math.random() * 150;
          scene.time.delayedCall(delay, () => {
            if (!b.active) return;
            const color = Math.random() > 0.5 ? 0xff6600 : 0xffaa00;
            const spark = scene.add.circle(
              b.x + (Math.random() - 0.5) * 12,
              b.y + (Math.random() - 0.5) * 8,
              1.5 + Math.random() * 2,
              color, 0.8,
            ).setDepth(b.depth + 1);
            scene.tweens.add({
              targets: spark,
              y: spark.y - 12 - Math.random() * 8,
              alpha: 0,
              duration: 300 + Math.random() * 200,
              onComplete: () => spark.destroy(),
            });
          });
        }
      } else {
        // Chasm fall: shrink to point + spin + darken fast
        scene.tweens.add({
          targets: b,
          scaleX: 0,
          scaleY: 0,
          alpha: 0,
          angle: b.angle + 360,
          duration: ms,
          ease: 'Cubic.easeIn',
        });
      }

      // Hide HP bar, label, and shadow during terrain death
      scene.tweens.add({
        targets: [enemy.hpBar, enemy.hpBarBg, enemy.label],
        alpha: 0,
        duration: ms * 0.5,
      });
      if (enemy.groundShadow) {
        scene.tweens.add({ targets: enemy.groundShadow, alpha: 0, duration: ms * 0.5 });
      }

      // Floating death cause label
      const labelMap = { water: { text: 'drowned', color: '#4488ff' }, magma: { text: 'burned', color: '#ff4400' }, chasm: { text: 'fell', color: '#222222' } } as const;
      const info = labelMap[type];
      const deathLabel = scene.add.text(b.x, b.y - 12, info.text, {
        fontSize: '9px', fontFamily: 'monospace', fontStyle: 'bold',
        color: info.color, stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5, 1).setDepth(b.depth + 2).setAlpha(0);
      scene.tweens.add({
        targets: deathLabel,
        alpha: 1, y: deathLabel.y - 16,
        duration: 300, ease: 'Back.easeOut',
        onComplete: () => {
          scene.tweens.add({
            targets: deathLabel, alpha: 0,
            delay: 600, duration: 400,
            onComplete: () => deathLabel.destroy(),
          });
        },
      });
    };
    EventBus.on(GameEvents.TERRAIN_DEATH, JuiceEffects._onTerrainDeath);

    // ── Easter egg: floating warning text (speed meltdown) ──
    JuiceEffects._onFloatingWarning = (data: { text: string; scene: Phaser.Scene }) => {
      const s = data.scene || scene;
      const x = 100 + Math.random() * (s.scale.width - 200);
      const y = 80 + Math.random() * (s.scale.height - 160);
      const txt = s.add.text(x, y, data.text, {
        fontSize: '14px', fontFamily: 'monospace', fontStyle: 'bold',
        color: '#ff4444', stroke: '#000000', strokeThickness: 3,
      }).setDepth(200).setOrigin(0.5).setAlpha(0);
      s.tweens.add({
        targets: txt,
        alpha: 1,
        duration: 150,
        yoyo: true,
        hold: 1500,
        onComplete: () => txt.destroy(),
      });
    };
    EventBus.on(GameEvents.EASTER_EGG_FLOATING_WARNING, JuiceEffects._onFloatingWarning);

    // ── Billiard knockback VFX ──

    JuiceEffects._onBilliardWallBounce = (data: { enemy: { body: Phaser.GameObjects.Image }; x: number; y: number; speed: number }) => {
      const { x, y, speed } = data;
      // Spark particles at impact point
      const intensity = Math.min(1, speed / 300);
      const count = 3 + Math.floor(intensity * 5);
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 8 + Math.random() * 12;
        const p = scene.add.circle(x, y, 1.5, 0xffaa44, 0.9).setDepth(25);
        scene.tweens.add({
          targets: p,
          x: x + Math.cos(angle) * dist,
          y: y + Math.sin(angle) * dist,
          alpha: 0, scaleX: 0.2, scaleY: 0.2,
          duration: 150 + Math.random() * 100,
          onComplete: () => p.destroy(),
        });
      }
      // Screen shake proportional to impact speed
      if (intensity > 0.3) JuiceEffects.screenShake(scene, 0.002 * intensity, 60);
      SoundManager.play('robotClank');
    };
    EventBus.on(GameEvents.BILLIARD_WALL_BOUNCE, JuiceEffects._onBilliardWallBounce);

    JuiceEffects._onBilliardEnemyCollision = (data: { enemyA: { body: Phaser.GameObjects.Image }; enemyB: { body: Phaser.GameObjects.Image }; x: number; y: number; relativeSpeed: number }) => {
      const { x, y, relativeSpeed } = data;
      const intensity = Math.min(1, relativeSpeed / 400);
      // Impact flash at collision midpoint
      const flash = scene.add.circle(x, y, 6 + intensity * 8, 0xffffff, 0.7).setDepth(26);
      scene.tweens.add({
        targets: flash,
        alpha: 0, scaleX: 2, scaleY: 2,
        duration: 120,
        onComplete: () => flash.destroy(),
      });
      // Particle burst
      const count = 4 + Math.floor(intensity * 6);
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 10 + Math.random() * 15;
        const p = scene.add.circle(x, y, 2, 0xffdd66, 0.8).setDepth(25);
        scene.tweens.add({
          targets: p,
          x: x + Math.cos(angle) * dist,
          y: y + Math.sin(angle) * dist,
          alpha: 0,
          duration: 180 + Math.random() * 80,
          onComplete: () => p.destroy(),
        });
      }
      if (intensity > 0.2) JuiceEffects.screenShake(scene, 0.003 * intensity, 80);
      SoundManager.play('robotClank');
    };
    EventBus.on(GameEvents.BILLIARD_ENEMY_COLLISION, JuiceEffects._onBilliardEnemyCollision);

    JuiceEffects._onBilliardKnockbackStart = (_data: { enemy: { body: Phaser.GameObjects.Image }; vx: number; vy: number; source: string }) => {
      SoundManager.play('hit');
    };
    EventBus.on(GameEvents.BILLIARD_KNOCKBACK_START, JuiceEffects._onBilliardKnockbackStart);

    JuiceEffects._onBilliardKnockbackEnd = (data: { enemy: { body: Phaser.GameObjects.Image } }) => {
      const b = data.enemy.body;
      if (!b || !b.active) return;
      // Small dust puff at rest position
      for (let i = 0; i < 3; i++) {
        const angle = Math.random() * Math.PI * 2;
        const p = scene.add.circle(b.x, b.y, 2, 0x886644, 0.4).setDepth(24);
        scene.tweens.add({
          targets: p,
          x: b.x + Math.cos(angle) * 10,
          y: b.y + Math.sin(angle) * 10,
          alpha: 0,
          duration: 300,
          onComplete: () => p.destroy(),
        });
      }
    };
    EventBus.on(GameEvents.BILLIARD_KNOCKBACK_END, JuiceEffects._onBilliardKnockbackEnd);

    // ── Easter egg: CRT glitch (triggered by console `hack` command) ──
    JuiceEffects._onCrtGlitch = (data: { duration: number }) => {
      const cam = scene.cameras.main;
      if (!cam) return;
      const ms = data.duration || 500;
      // Quick flash + shake
      cam.flash(ms * 0.6, 0, 255, 0, true);  // green flash
      JuiceEffects.screenShake(scene, 0.008, ms);
      // Random chromatic-style tint flickers
      const flickerCount = 6;
      for (let i = 0; i < flickerCount; i++) {
        scene.time.delayedCall(i * (ms / flickerCount), () => {
          const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xff00ff, 0x00ffff];
          const c = colors[Math.floor(Math.random() * colors.length)];
          cam.setBackgroundColor(c);
          scene.time.delayedCall(30, () => cam.setBackgroundColor(0x000000));
        });
      }
    };
    EventBus.on(GameEvents.EASTER_EGG_CRT_GLITCH, JuiceEffects._onCrtGlitch);
  }

  // ── Disconnect events (call on scene shutdown) ──
  static disconnectEvents(): void {
    if (JuiceEffects._onEnemySpawned) EventBus.off(GameEvents.ENEMY_SPAWNED, JuiceEffects._onEnemySpawned);
    if (JuiceEffects._onEnemyDestroyed) EventBus.off(GameEvents.ENEMY_DESTROYED, JuiceEffects._onEnemyDestroyed);
    if (JuiceEffects._onEnemyReachedBase) EventBus.off(GameEvents.ENEMY_REACHED_BASE, JuiceEffects._onEnemyReachedBase);
    if (JuiceEffects._onBaseDamaged) EventBus.off(GameEvents.BASE_DAMAGED, JuiceEffects._onBaseDamaged);
    if (JuiceEffects._onWaveStarted) EventBus.off(GameEvents.WAVE_STARTED, JuiceEffects._onWaveStarted);
    if (JuiceEffects._onRobotPlaced) EventBus.off(GameEvents.ROBOT_PLACED, JuiceEffects._onRobotPlaced);
    if (JuiceEffects._onRobotExploded) EventBus.off(GameEvents.ROBOT_EXPLODED, JuiceEffects._onRobotExploded);
    if (JuiceEffects._onKamikazeWarCry) EventBus.off(GameEvents.KAMIKAZE_WAR_CRY, JuiceEffects._onKamikazeWarCry);
    if (JuiceEffects._onPursueActivated) EventBus.off(GameEvents.PURSUE_ACTIVATED, JuiceEffects._onPursueActivated);
    if (JuiceEffects._onMissileExploded) EventBus.off(GameEvents.MISSILE_EXPLODED, JuiceEffects._onMissileExploded);
    if (JuiceEffects._onRobotFired) EventBus.off(GameEvents.ROBOT_FIRED, JuiceEffects._onRobotFired);
    if (JuiceEffects._onKillStreak) EventBus.off(GameEvents.KILL_STREAK, JuiceEffects._onKillStreak);
    if (JuiceEffects._onWaveCelebration) EventBus.off(GameEvents.WAVE_CELEBRATION, JuiceEffects._onWaveCelebration);
    if (JuiceEffects._onComponentUnlocked) EventBus.off(GameEvents.COMPONENT_UNLOCKED, JuiceEffects._onComponentUnlocked);
    if (JuiceEffects._onCriticalHit) EventBus.off(GameEvents.CRITICAL_HIT, JuiceEffects._onCriticalHit);
    if (JuiceEffects._onJumperPush) EventBus.off(GameEvents.JUMPER_PUSH, JuiceEffects._onJumperPush);
    if (JuiceEffects._onTerrainDeath) EventBus.off(GameEvents.TERRAIN_DEATH, JuiceEffects._onTerrainDeath);
    if (JuiceEffects._onFloatingWarning) EventBus.off(GameEvents.EASTER_EGG_FLOATING_WARNING, JuiceEffects._onFloatingWarning);
    if (JuiceEffects._onBilliardWallBounce) EventBus.off(GameEvents.BILLIARD_WALL_BOUNCE, JuiceEffects._onBilliardWallBounce);
    if (JuiceEffects._onBilliardEnemyCollision) EventBus.off(GameEvents.BILLIARD_ENEMY_COLLISION, JuiceEffects._onBilliardEnemyCollision);
    if (JuiceEffects._onBilliardKnockbackStart) EventBus.off(GameEvents.BILLIARD_KNOCKBACK_START, JuiceEffects._onBilliardKnockbackStart);
    if (JuiceEffects._onBilliardKnockbackEnd) EventBus.off(GameEvents.BILLIARD_KNOCKBACK_END, JuiceEffects._onBilliardKnockbackEnd);
    if (JuiceEffects._onCrtGlitch) EventBus.off(GameEvents.EASTER_EGG_CRT_GLITCH, JuiceEffects._onCrtGlitch);

    // Null out references so closures over scene can be GC'd
    JuiceEffects._onEnemySpawned = null;
    JuiceEffects._onEnemyDestroyed = null;
    JuiceEffects._onEnemyReachedBase = null;
    JuiceEffects._onBaseDamaged = null;
    JuiceEffects._onWaveStarted = null;
    JuiceEffects._onRobotPlaced = null;
    JuiceEffects._onRobotExploded = null;
    JuiceEffects._onKamikazeWarCry = null;
    JuiceEffects._onPursueActivated = null;
    JuiceEffects._onMissileExploded = null;
    JuiceEffects._onRobotFired = null;
    JuiceEffects._onKillStreak = null;
    JuiceEffects._onWaveCelebration = null;
    JuiceEffects._onComponentUnlocked = null;
    JuiceEffects._onCriticalHit = null;
    JuiceEffects._onJumperPush = null;
    JuiceEffects._onTerrainDeath = null;
    JuiceEffects._onFloatingWarning = null;
    JuiceEffects._onBilliardWallBounce = null;
    JuiceEffects._onBilliardEnemyCollision = null;
    JuiceEffects._onBilliardKnockbackStart = null;
    JuiceEffects._onBilliardKnockbackEnd = null;
    JuiceEffects._onCrtGlitch = null;
  }

  // ── Pursue Activation Burst ──
  // Expanding red ring + speed lines radiating outward when a robot enters pursue mode
  static pursueActivation(scene: Phaser.Scene, x: number, y: number, radius: number): void {
    // Expanding red/orange ring
    const ring = scene.add.circle(x, y, radius * 0.8, 0xff4400, 0)
      .setStrokeStyle(2.5, 0xff6600, 0.9).setDepth(25);
    scene.tweens.add({
      targets: ring,
      scaleX: 3, scaleY: 3,
      alpha: 0,
      duration: 350,
      ease: 'Quad.easeOut',
      onUpdate: () => ring.setStrokeStyle(2.5, 0xff6600, ring.alpha * 0.9),
      onComplete: () => ring.destroy(),
    });

    // Inner flash
    const flash = scene.add.circle(x, y, radius * 0.5, 0xff8800, 0.6)
      .setDepth(25).setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: flash,
      scaleX: 2.5, scaleY: 2.5, alpha: 0,
      duration: 250,
      ease: 'Quad.easeOut',
      onComplete: () => flash.destroy(),
    });

    // Speed lines (8 short streaks radiating outward)
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 * i) / 8 + (Math.random() - 0.5) * 0.3;
      const startR = radius * 0.6;
      const sx = x + Math.cos(angle) * startR;
      const sy = y + Math.sin(angle) * startR;
      const len = 8 + Math.random() * 10;

      const line = scene.add.rectangle(
        sx, sy, len, 1.5, 0xff6600, 0.8
      ).setDepth(25).setRotation(angle);

      scene.tweens.add({
        targets: line,
        x: sx + Math.cos(angle) * 25,
        y: sy + Math.sin(angle) * 25,
        alpha: 0,
        scaleX: 0.3,
        duration: 300 + Math.random() * 100,
        ease: 'Quad.easeOut',
        onComplete: () => line.destroy(),
      });
    }

    // Tiny red sparks
    JuiceEffects.particleBurst(scene, x, y, 0xff4400, 6, 40);
  }

  // ── Critical Hit Impact ──
  // Gold sparkle ring + subtle screen shake for crit feedback
  static critImpact(scene: Phaser.Scene, x: number, y: number): void {
    JuiceEffects.sparkleRing(scene, x, y, 0xffcc00, 18);
    JuiceEffects.screenShake(scene, 0.003, 60);
  }
}
